/* eslint-disable no-unneeded-ternary */
import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Button, Card, Grid2 as Grid, InputAdornment, OutlinedInput, Popper, Tooltip } from '@mui/material';
// third-party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';
import './index.css';
// project imports
import Transitions from 'ui-component/extended/Transitions';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
// assets
import { IconAdjustmentsHorizontal, IconClipboardCopy, IconPhysotherapist, IconSearch, IconX } from '@tabler/icons';
import { shouldForwardProp } from '@mui/system';
import usePermission from 'hooks/usePermission';
import PopoverNavigation from 'ui-component/PopoverNavigation';
import { Description, NoteAlt, NoteAltOutlined } from '@mui/icons-material';
import useAuth from 'hooks/useAuth';
import { getServiceIdByShortName, PHYSICIAN_CLIENT } from 'constant';
import AddMissedNote from 'views/therapy/Treatment/List/AddMissedNote';

// styles
const PopperStyle = styled(Popper, { shouldForwardProp })(({ theme }) => ({
    zIndex: 1100,
    width: '99%',
    top: '-55px !important',
    padding: '0 12px',
    [theme.breakpoints.down('sm')]: {
        padding: '0 10px'
    }
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
    width: 434,
    marginLeft: 16,
    paddingLeft: 16,
    paddingRight: 16,
    '& input': {
        background: 'transparent !important',
        paddingLeft: '4px !important'
    },
    [theme.breakpoints.down('lg')]: {
        width: 250
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
        marginLeft: 4,
        background: theme.palette.mode === 'dark' ? theme.palette.dark[800] : '#fff'
    }
}));

const HeaderAvatarStyle = styled(Avatar, { shouldForwardProp })(({ theme }) => ({
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
    color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
    '&:hover': {
        background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
        color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
    }
}));

// ==============================|| SEARCH INPUT - MOBILE||============================== //

const MobileSearch = ({ value, setValue, popupState }) => {
    const theme = useTheme();
    return (
        <OutlineInputStyle
            id="input-search-header"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search Patients"
            startAdornment={
                <InputAdornment position="start">
                    <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                </InputAdornment>
            }
            endAdornment={
                <InputAdornment position="end">
                    <HeaderAvatarStyle variant="rounded">
                        <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
                    </HeaderAvatarStyle>
                    <Box sx={{ ml: 2 }}>
                        <Avatar
                            variant="rounded"
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.orange.light,
                                color: theme.palette.orange.dark,
                                '&:hover': {
                                    background: theme.palette.orange.dark,
                                    color: theme.palette.orange.light
                                }
                            }}
                            {...bindToggle(popupState)}
                        >
                            <IconX stroke={1.5} size="20px" />
                        </Avatar>
                    </Box>
                </InputAdornment>
            }
            aria-describedby="search-helper-text"
            inputProps={{ 'aria-label': 'weight' }}
        />
    );
};

MobileSearch.propTypes = {
    value: PropTypes.string,
    setValue: PropTypes.func,
    popupState: PopupState
};

// ==============================|| SEARCH INPUT ||============================== //

const serviceList = [
    {
        id: 0,
        label: 'Speech Therapy',
        shorthand: 'st',
        permission: ['st_eval_view', 'st_note_view']
    },
    {
        id: 1,
        label: 'Physical Therapy',
        shorthand: 'pt',
        permission: ['pt_eval_view', 'pt_note_view']
    },
    {
        id: 2,
        label: 'Occupational Therapy',
        shorthand: 'ot',
        permission: ['ot_eval_view', 'ot_note_view']
    },
    {
        id: 3,
        label: 'Developmental Therapy',
        shorthand: 'dt',
        permission: ['dt_eval_view', 'dt_note_view']
    }
];

const quickActionServiceList = [
    {
        id: 'personal-care',
        label: 'Personal Care',
        service: 'PCS',
        permissions: ['personal_care_add'],
        url: '/add-personal-care'
    },
    {
        id: 'psychological-service',
        label: 'Psychological Service',
        service: 'PS',
        permissions: ['psychological_service_add'],
        url: '/psychologicalservice/create'
    },
    {
        id: 'counseling-service',
        label: 'Counseling',
        service: 'COUNSELING',
        permissions: ['counseling_add'],
        url: '/counseling/create'
    },
    {
        id: 'psychological-testing',
        label: 'Psychological Testing',
        service: 'PST',
        permissions: ['psychological_testing_add'],
        url: '/psychologicaltesting/add'
    },
    {
        id: 'nursing',
        label: 'Nursing',
        service: 'NURSING',
        permissions: ['nursing_add'],
        url: '/nursing/create'
    }
];

const quickActionsPermissions = [
    'st_eval_view',
    'st_note_view',
    'pt_eval_view',
    'pt_note_view',
    'ot_eval_view',
    'ot_note_view',
    'dt_eval_view',
    'dt_note_view',
    ...quickActionServiceList.flatMap((item) => item.permissions)
];

const SearchSection = () => {
    const { user } = useAuth();
    const { userPermissions, hasPermission, isSuperAdmin, hasServiceAvailable } = usePermission();
    const theme = useTheme();
    const [value, setValue] = useState('');
    const [popoverOpen, setPopoverOpen] = useState(false);
    const anchorEl = useRef(null);
    const navigate = useNavigate();

    const [missedNoteOpen, setMissedNoteOpen] = useState(false);
    const [service, setService] = useState(null);

    const openPopover = () => {
        setPopoverOpen(true);
    };

    const closePopover = () => {
        setPopoverOpen(false);
    };

    const hasQuickActionPermission = quickActionsPermissions.filter((p) => userPermissions?.includes(p));
    const hasVisibleQuickAction =
        serviceList.some((item) => {
            const hasItemPermission = item?.permission?.some((p) => hasQuickActionPermission.includes(p));
            return hasItemPermission && (isSuperAdmin || hasServiceAvailable(item?.shorthand));
        }) ||
        quickActionServiceList.some((item) => {
            const hasItemPermission = item?.permissions?.some((p) => hasQuickActionPermission.includes(p));
            return hasItemPermission && (isSuperAdmin || hasServiceAvailable(item?.service));
        });

    return (
        <>
            {/*
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <PopupState variant="popper" popupId="demo-popup-popper">
                    {(popupState) => (
                        <>
                            <Box sx={{ ml: 2 }}>
                                <HeaderAvatarStyle variant="rounded" {...bindToggle(popupState)}>
                                    <IconSearch stroke={1.5} size="19px" />
                                </HeaderAvatarStyle>
                            </Box>
                            <PopperStyle {...bindPopper(popupState)} transition>
                                {({ TransitionProps }) => (
                                    <>
                                        <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                                            <Card
                                                sx={{
                                                    background: theme.palette.mode === 'dark' ? theme.palette.dark[900] : '#fff',
                                                    [theme.breakpoints.down('sm')]: {
                                                        border: 0,
                                                        boxShadow: 'none'
                                                    }
                                                }}
                                            >
                                                <Box sx={{ p: 2 }}>
                                                    <Grid container alignItems="center" justifyContent="space-between">
                                                        <Grid item xs>
                                                            <MobileSearch value={value} setValue={setValue} popupState={popupState} />
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Card>
                                        </Transitions>
                                    </>
                                )}
                            </PopperStyle>
                        </>
                    )}
                </PopupState>
            </Box>
      */}
            {/* <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <OutlineInputStyle
                    id="input-search-header"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Search Patients"
                    startAdornment={
                        <InputAdornment position="start">
                            <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                        </InputAdornment>
                    }
                    // endAdornment={
                    //     <InputAdornment position="end">
                    //         <HeaderAvatarStyle variant="rounded">
                    //             <IconAdjustmentsHorizontal stroke={1.5} size="20px" />
                    //         </HeaderAvatarStyle>
                    //     </InputAdornment>
                    // }
                    aria-describedby="search-helper-text"
                    inputProps={{ 'aria-label': 'weight' }}
                />
            </Box> */}
            <Box sx={{ display: { xs: 'none', md: 'block', mb: 0.5 } }}>
                {hasPermission(['patient_add']) && !(user?.is_superuser && user?.client_id === PHYSICIAN_CLIENT) && (
                    <Tooltip placement="top" title="Add Patient">
                        <Avatar
                            variant="rounded"
                            style={{ marginLeft: 10 }}
                            sx={{
                                ...theme.typography.commonAvatar,
                                ...theme.typography.mediumAvatar,
                                transition: 'all .2s ease-in-out',
                                background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                                color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                '&[aria-controls="menu-list-grow"],&:hover': {
                                    background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                    color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                                }
                            }}
                            aria-haspopup="true"
                            onClick={() => {
                                navigate('/add-patient');
                            }}
                            color="inherit"
                        >
                            <PersonAddAlt1Icon stroke={1.5} size="30px" />
                        </Avatar>
                    </Tooltip>
                )}
            </Box>
            {hasVisibleQuickAction && !(user?.is_superuser && user?.client_id === PHYSICIAN_CLIENT) && (
                <Box
                    onMouseOver={openPopover}
                    onMouseLeave={closePopover}
                    ref={anchorEl}
                    sx={{ display: { xs: 'none', md: 'block', mb: 0.5 } }}
                >
                    <Avatar
                        variant="rounded"
                        style={{ marginLeft: 10 }}
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                            '&[aria-controls="menu-list-grow"],&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                            }
                        }}
                        aria-haspopup="true"
                        color="inherit"
                    >
                        <IconClipboardCopy stroke={1.5} size="30px" />
                    </Avatar>
                    <PopoverNavigation
                        open={popoverOpen}
                        anchorEl={anchorEl.current}
                        openPopover={openPopover}
                        closePopover={closePopover}
                        innerList={serviceList}
                        directList={quickActionServiceList}
                        permissionsList={hasQuickActionPermission}
                        setMissedNoteOpen={setMissedNoteOpen}
                        setService={setService}
                    />
                </Box>
            )}
            <AddMissedNote
                open={missedNoteOpen}
                setMissedNoteOpen={setMissedNoteOpen}
                serviceId={getServiceIdByShortName(service)}
                type="Quick Action"
            />
        </>
    );
};

export default SearchSection;
